import React, { useState } from 'react';
import { Check, Download } from 'lucide-react';
import { sans, mono, green, greenLight, useTheme } from '../../tokens';

const sectionTitleStyle = (t) => ({
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: t.textMuted,
  fontFamily: sans,
  marginBottom: 16,
  paddingBottom: 8,
  borderBottom: '1px solid ' + t.border,
});

const CURRENT_PLAN = {
  name: 'Professional',
  usersUsed: 5,
  usersMax: 25,
  storageUsed: 2.4,
  storageMax: 10,
  features: [
    'Unlimited close checklists',
    'Equity module with SBC calculator',
    'Investor portal',
    'FP&A dashboards',
    'Audit trail (7-year retention)',
    'Priority support',
  ],
};

const BILLING_HISTORY = [
  { id: 1, date: '2026-03-01', description: 'Professional Plan - March 2026', amount: 299.00, status: 'paid' },
  { id: 2, date: '2026-02-01', description: 'Professional Plan - February 2026', amount: 299.00, status: 'paid' },
  { id: 3, date: '2026-01-01', description: 'Professional Plan - January 2026', amount: 299.00, status: 'paid' },
  { id: 4, date: '2025-12-01', description: 'Professional Plan - December 2025', amount: 299.00, status: 'paid' },
  { id: 5, date: '2025-11-01', description: 'Professional Plan - November 2025', amount: 299.00, status: 'paid' },
];

const PLANS = [
  {
    name: 'Free',
    price: 0,
    features: ['1 user', '1 close checklist', 'Basic dashboard', '100 MB storage'],
    recommended: false,
  },
  {
    name: 'Starter',
    price: 99,
    features: ['5 users', 'Unlimited close checklists', 'FP&A dashboards', '2 GB storage', 'Email support'],
    recommended: false,
  },
  {
    name: 'Professional',
    price: 299,
    features: ['25 users', 'All modules', 'Investor portal', 'Equity & SBC', '10 GB storage', 'Priority support', 'Audit trail'],
    recommended: true,
  },
  {
    name: 'Enterprise',
    price: null,
    features: ['Unlimited users', 'All modules', 'SSO / SAML', 'Custom integrations', 'Unlimited storage', 'Dedicated support', 'SLA'],
    recommended: false,
  },
];

export default function BillingSettings() {
  const t = useTheme();
  const savedBilling = JSON.parse(localStorage.getItem('finkloz_billing_settings') || 'null');
  const [selectedPlan, setSelectedPlan] = useState(savedBilling?.selectedPlan || 'Professional');
  const [showUpdatePayment, setShowUpdatePayment] = useState(false);
  const [cardLast4, setCardLast4] = useState(savedBilling?.cardLast4 || '4242');
  const [cardExpiry, setCardExpiry] = useState(savedBilling?.cardExpiry || '12/27');
  const [paymentForm, setPaymentForm] = useState({ last4: '', expiryMonth: '', expiryYear: '' });
  const [planConfirm, setPlanConfirm] = useState(null);

  const persistBilling = (overrides) => {
    const data = { selectedPlan, cardLast4, cardExpiry, ...overrides };
    localStorage.setItem('finkloz_billing_settings', JSON.stringify(data));
  };

  const usagePercent = (used, max) => Math.round((used / max) * 100);

  const handleSavePayment = () => {
    const { last4, expiryMonth, expiryYear } = paymentForm;
    if (!last4 || last4.length !== 4 || !/^\d{4}$/.test(last4)) return;
    if (!expiryMonth || !expiryYear) return;
    const expiry = expiryMonth.padStart(2, '0') + '/' + expiryYear.slice(-2);
    setCardLast4(last4);
    setCardExpiry(expiry);
    persistBilling({ cardLast4: last4, cardExpiry: expiry });
    setShowUpdatePayment(false);
    setPaymentForm({ last4: '', expiryMonth: '', expiryYear: '' });
  };

  const handleDownloadInvoice = (invoice) => {
    const lines = [
      'FinKloz Invoice',
      '===============',
      '',
      'Date:        ' + invoice.date,
      'Description: ' + invoice.description,
      'Amount:      $' + invoice.amount.toFixed(2) + ' CAD',
      'Status:      ' + invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1),
      '',
      '---',
      'FinKloz Inc.',
      'finkloz.com',
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const monthStr = invoice.date.slice(0, 7);
    a.download = 'finkloz_invoice_' + monthStr + '.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePlanSelect = (plan) => {
    if (plan.name === selectedPlan || plan.price === null) return;
    setPlanConfirm(plan.name);
  };

  const confirmPlanSwitch = () => {
    if (!planConfirm) return;
    setSelectedPlan(planConfirm);
    persistBilling({ selectedPlan: planConfirm });
    setPlanConfirm(null);
  };

  const inputStyle = {
    fontFamily: sans,
    fontSize: 12,
    padding: '6px 10px',
    border: '1px solid ' + t.border,
    borderRadius: 4,
    background: t.inputBg,
    color: t.textPrimary,
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ maxWidth: 900 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans, margin: '0 0 4px' }}>
        Billing
      </h2>
      <p style={{ fontSize: 12, color: t.textMuted, fontFamily: sans, margin: '0 0 24px' }}>
        Current plan, payment method, and billing history.
      </p>

      {/* Current Plan */}
      <div style={sectionTitleStyle(t)}>Current Plan</div>

      <div style={{
        border: '1px solid ' + green,
        borderRadius: 8,
        padding: 20,
        marginBottom: 32,
        background: t.bgCard,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: t.textPrimary, fontFamily: sans }}>
              {selectedPlan}
            </div>
            <div style={{ fontSize: 12, color: t.textMuted, fontFamily: sans, marginTop: 2 }}>
              ${PLANS.find(p => p.name === selectedPlan)?.price ?? 'Custom'}/month
            </div>
          </div>
          <span style={{
            padding: '4px 10px', borderRadius: 4, fontSize: 10, fontWeight: 600,
            textTransform: 'uppercase', background: greenLight, color: green,
          }}>
            Active
          </span>
        </div>

        {/* Usage bars */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 11, color: t.textSecondary, fontFamily: sans }}>Users</span>
              <span style={{ fontSize: 11, color: t.textSecondary, fontFamily: mono }}>
                {CURRENT_PLAN.usersUsed}/{CURRENT_PLAN.usersMax}
              </span>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: t.pillBg, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: usagePercent(CURRENT_PLAN.usersUsed, CURRENT_PLAN.usersMax) + '%',
                borderRadius: 3,
                background: green,
                transition: 'width 0.3s',
              }} />
            </div>
          </div>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 11, color: t.textSecondary, fontFamily: sans }}>Storage</span>
              <span style={{ fontSize: 11, color: t.textSecondary, fontFamily: mono }}>
                {CURRENT_PLAN.storageUsed} GB/{CURRENT_PLAN.storageMax} GB
              </span>
            </div>
            <div style={{ height: 6, borderRadius: 3, background: t.pillBg, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: usagePercent(CURRENT_PLAN.storageUsed, CURRENT_PLAN.storageMax) + '%',
                borderRadius: 3,
                background: green,
                transition: 'width 0.3s',
              }} />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {CURRENT_PLAN.features.map(f => (
            <div key={f} style={{
              display: 'flex', alignItems: 'center', gap: 4,
              fontSize: 11, color: t.textSecondary, fontFamily: sans,
            }}>
              <Check size={12} style={{ color: green }} />
              {f}
            </div>
          ))}
        </div>
      </div>

      {/* Payment Method */}
      <div style={sectionTitleStyle(t)}>Payment Method</div>

      <div style={{
        border: '1px solid ' + t.border, borderRadius: 8,
        padding: 16, marginBottom: showUpdatePayment ? 0 : 32, background: t.bgCard,
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 44, height: 28, borderRadius: 4,
              background: '#1A237E', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, color: '#fff', fontFamily: sans, letterSpacing: 0.5,
            }}>
              VISA
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, color: t.textPrimary, fontFamily: sans }}>
                Visa ending in {cardLast4}
              </div>
              <div style={{ fontSize: 11, color: t.textMuted, fontFamily: mono }}>
                Expires {cardExpiry}
              </div>
            </div>
          </div>
          <button
            onClick={() => setShowUpdatePayment(!showUpdatePayment)}
            style={{
              padding: '6px 16px', border: '1px solid ' + t.border, borderRadius: 6,
              background: 'transparent', color: t.textSecondary, fontSize: 11, fontWeight: 600,
              fontFamily: sans, cursor: 'pointer',
            }}
          >
            {showUpdatePayment ? 'Cancel' : 'Update'}
          </button>
        </div>
      </div>

      {showUpdatePayment && (
        <div style={{
          border: '1px solid ' + t.border, borderTop: 'none',
          borderRadius: '0 0 8px 8px',
          padding: 16, marginBottom: 32, background: t.bgSurface,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: t.textSecondary, fontFamily: sans, marginBottom: 10 }}>
            Update Payment Method (Mock)
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: t.textMuted, fontFamily: sans, display: 'block', marginBottom: 4 }}>
                Last 4 Digits
              </label>
              <input
                type="text"
                maxLength={4}
                placeholder="1234"
                value={paymentForm.last4}
                onChange={e => setPaymentForm(p => ({ ...p, last4: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                style={{ ...inputStyle, width: 80 }}
              />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: t.textMuted, fontFamily: sans, display: 'block', marginBottom: 4 }}>
                Exp Month
              </label>
              <select
                value={paymentForm.expiryMonth}
                onChange={e => setPaymentForm(p => ({ ...p, expiryMonth: e.target.value }))}
                style={{ ...inputStyle, width: 70 }}
              >
                <option value="">--</option>
                {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 600, color: t.textMuted, fontFamily: sans, display: 'block', marginBottom: 4 }}>
                Exp Year
              </label>
              <select
                value={paymentForm.expiryYear}
                onChange={e => setPaymentForm(p => ({ ...p, expiryYear: e.target.value }))}
                style={{ ...inputStyle, width: 80 }}
              >
                <option value="">--</option>
                {['2026', '2027', '2028', '2029', '2030', '2031'].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <button
              onClick={handleSavePayment}
              style={{
                padding: '6px 16px', border: 'none', borderRadius: 6,
                background: green, color: '#fff', fontSize: 11, fontWeight: 600,
                fontFamily: sans, cursor: 'pointer',
              }}
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* Billing History */}
      <div style={sectionTitleStyle(t)}>Billing History</div>

      <div style={{
        border: '1px solid ' + t.border,
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 32,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sans, fontSize: 12 }}>
          <thead>
            <tr style={{ background: t.bgSurface }}>
              {['Date', 'Description', 'Amount', 'Status', ''].map(h => (
                <th key={h || 'action'} style={{
                  padding: '10px 14px',
                  textAlign: h === 'Amount' ? 'right' : 'left',
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: t.textMuted,
                  borderBottom: '1px solid ' + t.border,
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {BILLING_HISTORY.map(b => (
              <tr key={b.id} style={{ borderBottom: '1px solid ' + t.borderLight }}>
                <td style={{ padding: '10px 14px', color: t.textSecondary, fontFamily: mono, fontSize: 11 }}>
                  {b.date}
                </td>
                <td style={{ padding: '10px 14px', color: t.textPrimary, fontWeight: 500 }}>
                  {b.description}
                </td>
                <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: mono, fontSize: 11, color: t.textPrimary }}>
                  ${b.amount.toFixed(2)}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{
                    padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                    textTransform: 'uppercase', background: greenLight, color: green,
                  }}>
                    {b.status}
                  </span>
                </td>
                <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                  <button
                    onClick={() => handleDownloadInvoice(b)}
                    title={'Download invoice ' + b.date}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: t.textMuted, padding: 4,
                    }}
                  >
                    <Download size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Plan Switch Confirmation */}
      {planConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            background: t.bgCard, border: '1px solid ' + t.border, borderRadius: 10,
            padding: 28, maxWidth: 400, width: '90%', fontFamily: sans,
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 8 }}>
              Switch to {planConfirm}?
            </div>
            <div style={{ fontSize: 12, color: t.textSecondary, marginBottom: 6 }}>
              Your plan will change from {selectedPlan} to {planConfirm} at ${PLANS.find(p => p.name === planConfirm)?.price}/month.
            </div>
            <div style={{ fontSize: 11, color: t.textMuted, marginBottom: 20, fontStyle: 'italic' }}>
              Changes take effect at next billing cycle.
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setPlanConfirm(null)}
                style={{
                  padding: '7px 18px', border: '1px solid ' + t.border, borderRadius: 6,
                  background: 'transparent', color: t.textSecondary, fontSize: 12, fontWeight: 600,
                  fontFamily: sans, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmPlanSwitch}
                style={{
                  padding: '7px 18px', border: 'none', borderRadius: 6,
                  background: green, color: '#fff', fontSize: 12, fontWeight: 600,
                  fontFamily: sans, cursor: 'pointer',
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Options */}
      <div style={sectionTitleStyle(t)}>Plan Options</div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
        {PLANS.map(plan => (
          <div key={plan.name} style={{
            border: plan.name === selectedPlan ? '2px solid ' + green : '1px solid ' + t.border,
            borderRadius: 8,
            padding: 20,
            background: t.bgCard,
            position: 'relative',
          }}>
            {plan.name === selectedPlan && (
              <div style={{
                position: 'absolute', top: -10, left: '50%', transform: 'translateX(-50%)',
                padding: '2px 10px', borderRadius: 4, fontSize: 9, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: 0.5,
                background: green, color: '#fff', fontFamily: sans,
              }}>
                Current
              </div>
            )}
            <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: sans, marginBottom: 4 }}>
              {plan.name}
            </div>
            <div style={{ fontSize: 20, fontWeight: 700, color: t.textPrimary, fontFamily: mono, marginBottom: 12 }}>
              {plan.price !== null ? `$${plan.price}` : 'Custom'}
              {plan.price !== null && <span style={{ fontSize: 11, fontWeight: 400, color: t.textMuted }}>/mo</span>}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {plan.features.map(f => (
                <div key={f} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 6,
                  fontSize: 11, color: t.textSecondary, fontFamily: sans,
                }}>
                  <Check size={12} style={{ color: green, marginTop: 1, flexShrink: 0 }} />
                  {f}
                </div>
              ))}
            </div>
            <button
              onClick={() => handlePlanSelect(plan)}
              style={{
              marginTop: 16, width: '100%',
              padding: '8px 16px',
              border: plan.name === selectedPlan ? 'none' : '1px solid ' + t.border,
              borderRadius: 6,
              background: plan.name === selectedPlan ? t.pillBg : 'transparent',
              color: plan.name === selectedPlan ? t.textMuted : t.textSecondary,
              fontSize: 11,
              fontWeight: 600,
              fontFamily: sans,
              cursor: plan.name === selectedPlan ? 'default' : 'pointer',
            }}>
              {plan.name === selectedPlan ? 'Current Plan' : plan.price === null ? 'Contact Sales' : 'Select'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
